import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { portalApi } from '../services/api'
import { useAuth } from '../context/AuthContext'
import { useLanguage } from '../context/LanguageContext'

export default function Signup() {
  const navigate = useNavigate()
  const { login: setAuth } = useAuth()
  const { t } = useLanguage()

  const [form, setForm] = useState({
    courseName: '',
    website: '',
    phone: '',
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  function update(key, val) {
    setForm(f => ({ ...f, [key]: val }))
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')

    // Client-side validation
    if (form.password !== form.confirmPassword) {
      return setError(t('sp_signup_passwords_no_match', 'Passwords do not match'))
    }
    if (form.password.length < 8) {
      return setError(t('sp_signup_password_min_length', 'Password must be at least 8 characters'))
    }
    if (!/[A-Z]/.test(form.password) || !/[a-z]/.test(form.password) || !/[0-9]/.test(form.password)) {
      return setError(t('sp_signup_password_complexity', 'Password must contain an uppercase letter, a lowercase letter, and a number'))
    }

    setSubmitting(true)
    try {
      const { data } = await portalApi.post('/auth/signup', {
        courseName: form.courseName,
        website: form.website || undefined,
        phone: form.phone || undefined,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        password: form.password,
      })
      setAuth(data.user, data.token)
      navigate('/portal')
    } catch (err) {
      if (err.response?.data?.message) {
        setError(err.response.data.message)
      } else if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
        setError(t('sp_signup_server_unreachable', 'Could not reach the signup server. Please check that PlayStay-API is running on port 3011.'))
      } else {
        setError(err.message || t('sp_signup_failed_retry', 'Signup failed. Please try again.'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, var(--brand-50) 0%, #fff 50%, var(--brand-100) 100%)',
      padding: 20,
    }}>
      <div className="card" style={{ padding: 40, width: '100%', maxWidth: 520 }}>
        <Link to="/" style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
          <img src="/integolf-logo.svg" alt="InteGolf" style={{ height: 42 }} />
        </Link>

        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 11, letterSpacing: 2, color: 'var(--brand-600)', fontWeight: 700, marginBottom: 6 }}>
            {t('sp_play_and_stay_portal_caps', 'PLAY AND STAY PORTAL')}
          </div>
          <h1 style={{ margin: '0 0 6px', fontSize: 24, color: 'var(--secondary-950)' }}>{t('sp_create_your_portal', 'Create your portal')}</h1>
          <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 13 }}>
            {t('sp_signup_subtitle', 'Free to sign up. Takes about 5 minutes.')}
          </p>
        </div>

        {/* Google OAuth button */}
        <a
          href="/api/play-stay-portal/auth/google"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 10, padding: '12px', marginBottom: 16,
            background: '#fff', color: 'var(--secondary-800)',
            border: '1px solid var(--input-border)', borderRadius: 10,
            fontWeight: 600, fontSize: 14, textDecoration: 'none',
          }}
        >
          <GoogleIcon />
          {t('sp_sign_up_with_google', 'Sign up with Google')}
        </a>

        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          color: 'var(--text-muted)', fontSize: 12, margin: '16px 0 20px',
        }}>
          <div style={{ flex: 1, height: 1, background: 'var(--card-border)' }} />
          {t('sp_or_sign_up_with_email', 'or sign up with email')}
          <div style={{ flex: 1, height: 1, background: 'var(--card-border)' }} />
        </div>

        <form onSubmit={handleSubmit}>
          {error && (
            <div style={{
              padding: '10px 14px', marginBottom: 16,
              background: '#fef2f2', color: '#dc2626',
              border: '1px solid #fecaca', borderRadius: 8, fontSize: 13,
            }}>
              {error}
            </div>
          )}

          <h3 style={sectionStyle}>{t('course', 'Course')}</h3>
          <div style={rowStyle}>
            <Field label={t('sp_course_name_required', 'Course Name *')} value={form.courseName} onChange={v => update('courseName', v)} required />
          </div>
          <div style={rowStyle2}>
            <Field label={t('phone', 'Phone')} value={form.phone} onChange={v => update('phone', v)} type="tel" />
            <Field label={t('sp_website_optional', 'Website (optional)')} value={form.website} onChange={v => update('website', v)} placeholder="https://yourcourse.com" />
          </div>

          <h3 style={{ ...sectionStyle, marginTop: 20 }}>{t('sp_your_account', 'Your Account')}</h3>
          <div style={rowStyle2}>
            <Field label={t('sp_first_name_required', 'First Name *')} value={form.firstName} onChange={v => update('firstName', v)} required />
            <Field label={t('sp_last_name_required', 'Last Name *')} value={form.lastName} onChange={v => update('lastName', v)} required />
          </div>
          <div style={rowStyle}>
            <Field label={t('sp_email_required', 'Email *')} value={form.email} onChange={v => update('email', v)} type="email" required />
          </div>
          <div style={rowStyle2}>
            <Field label={t('sp_password_required', 'Password *')} value={form.password} onChange={v => update('password', v)} type="password" required />
            <Field label={t('sp_confirm_password_required', 'Confirm Password *')} value={form.confirmPassword} onChange={v => update('confirmPassword', v)} type="password" required />
          </div>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '4px 0 16px' }}>
            {t('sp_password_hint', 'Must be 8+ characters with uppercase, lowercase, and a number.')}
          </p>

          <button
            type="submit"
            disabled={submitting}
            className="btn btn-dark"
            style={{ width: '100%', padding: '14px', opacity: submitting ? 0.6 : 1 }}
          >
            {submitting ? t('sp_creating', 'Creating...') : t('sp_create_portal', 'Create Portal')}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: 20, fontSize: 14, color: 'var(--text-muted)' }}>
          {t('sp_already_have_account', 'Already have an account?')} <Link to="/login" style={{ fontWeight: 600, color: 'var(--brand-600)' }}>{t('sp_sign_in', 'Sign in')}</Link>
        </div>
      </div>
    </div>
  )
}

// ─── Small helpers ──────────────────────────────────────────────────────

const sectionStyle = {
  fontSize: 12, fontWeight: 700, letterSpacing: 1,
  color: 'var(--text-muted)', textTransform: 'uppercase',
  margin: '8px 0 10px',
}

const rowStyle = {
  marginBottom: 12,
}

const rowStyle2 = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 12,
  marginBottom: 12,
}

const labelStyle = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--secondary-800)',
  marginBottom: 6,
}

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid var(--input-border)',
  borderRadius: 8,
  fontSize: 14,
  outline: 'none',
  fontFamily: 'inherit',
  color: 'var(--text)',
  boxSizing: 'border-box',
}

function Field({ label, value, onChange, type = 'text', required, placeholder }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        style={inputStyle}
      />
    </div>
  )
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" />
      <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" />
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" />
    </svg>
  )
}
